import { observable, computed, action, runInAction } from "mobx";
import { LazyImportWithLoadFailHandle as lazy } from "@lib/lazy-import-with-guard/LazyImportWithGuard";

//#region stores

import { RootStore } from "../types";
import { ActiveEventsStore } from "./ActiveEventsStore";
import { ReuseBetSlipStore } from "./ReuseBetSlipStore";
import { BetCancelStore } from "./BetCancelStore";
import ShareBetSlipStore from "./ShareBetSlipStore";
import { LoaderStore } from "../common";
import MyBetsStore from "./MyBetsStore";

//#endregion stores

//#region helpers and models
import { CashoutType } from "@gp/hub";
import { getCurrentCulture, getWalletByShopIdAndAccountTypeId } from "@utils";
import { getEventIdsFromMyBets } from "@utils/my-bets/helpers";
import { analyticsTracker } from "@utils/analytics";
import { OpenCashoutModalType } from "@data-types/my-bets/OpenCashoutModal";
import { CashoutLocation } from "@utils/common/cashoutLocation";
import { BetViewType } from "./BaseMyBetsStore";
import { RequestContextType } from "@utils/api-config/RequestContext";
import { createApiRequestOptions } from "@utils/api-config/createApiRequestOptions";

//#endregion helpers and models

const loadFailPath = `/${getCurrentCulture()}/app-update`;

// prettier-ignore
const BetSlipService = lazy(loadFailPath, () =>import("@services/bet-slip/BetSlipService"))
// prettier-ignore
const  MyBetsService = lazy(loadFailPath, ()=>import("@services/my-bets/MyBetsService"));

export class BetDetailsModalStore {
	rootStore: RootStore;
	activeEventsStore: ActiveEventsStore;
	reuseBetSlipStore: ReuseBetSlipStore;
	betCancelStore: BetCancelStore;
	shareBetSlipStore: ShareBetSlipStore;
	loader: LoaderStore;
	myBetsStore: MyBetsStore;

	constructor(rootStore: RootStore, myBetsStore: MyBetsStore) {
		this.rootStore = rootStore;
		this.myBetsStore = myBetsStore;

		this.activeEventsStore = new ActiveEventsStore(rootStore);
		this.reuseBetSlipStore = new ReuseBetSlipStore(rootStore);
		this.betCancelStore = new BetCancelStore(rootStore, this);
		this.shareBetSlipStore = new ShareBetSlipStore(rootStore);
		this.loader = new LoaderStore();
	}

	//#region observables
	@observable isStoreInitialized: boolean = false;
	@observable isModalOpen: boolean = false;

	@observable walletId = "";
	@observable currentBetIndex: number = -1;
	@observable betCombinationsMap = observable.map();

	@observable isCashoutOpen: boolean = false;
	@observable isBetCancelOpen: boolean = false;
	@observable isAutoSellOpen: boolean = false;
	@observable isReuseOpen: boolean = false;
	@observable isShareActive: boolean = false;

	public lastUsedDirection: string | null = null;
	//#endregion observables

	@computed get currentBet(): BetViewType {
		return this.myBetsStore.betsList[this.currentBetIndex];
	}

	@computed get isFirst(): boolean {
		if (this.myBetsStore.pageNumber === 1 && this.currentBetIndex === 0) {
			return true;
		} else {
			return false;
		}
	}

	@computed get isLast(): boolean {
		if (
			this.myBetsStore.pageNumber === this.totalPages &&
			this.currentBetIndex === this.myBetsStore.betsList.length - 1
		) {
			return true;
		} else {
			return false;
		}
	}

	@computed get totalPages(): number {
		const totalItems = this.myBetsStore.totalItems;
		const pageSize = this.myBetsStore.pageSize;

		const remainder = totalItems % pageSize;
		const totalPages =
			remainder === 0
				? totalItems / pageSize
				: (totalItems - remainder) / pageSize + 1;

		return totalPages;
	}

	@computed get isMyBetsLoading(): boolean {
		return this.myBetsStore.loader.isLoading;
	}

	//#region actions
	@action.bound
	async onInitialize(walletId: string) {
		this.loader.suspend();
		this.walletId = walletId;

		runInAction(() => {
			this.loader.resume();
			this.isStoreInitialized = true;
		});
	}

	@action.bound
	async setCurrentBet(bet: BetViewType) {
		if (bet == null) {
			return;
		}

		this.loader.suspend();

		const eventIdsFromBet = getEventIdsFromMyBets([bet]);
		this.activeEventsStore.fetchActiveEvents(eventIdsFromBet);

		if (bet.combinationBettingOffersCount > 1) {
			await this.initBetCombinationForBetSlip(bet);
		}

		if (bet.needsAdditionalDetails) {
			let userAccount = getWalletByShopIdAndAccountTypeId(
				bet.shopId,
				bet.bettingAccountTypeId,
				this.rootStore.userAuthStore.userAccounts || []
			);

			let context = this.rootStore.userAuthStore.mapUserIdToUserContext(
				bet.userId
			);

			if (userAccount) {
				context = createApiRequestOptions(
					RequestContextType.Wallet,
					userAccount.id
				);
			}

			const details = await (
				await MyBetsService
			).default.getBet(
				bet.id,
				"betSlipOffers,player.coreUser,betSlipStatus,betStatus,betSlipType,betSlipSecureWinSetting",
				context
			);

			runInAction(() => {
				let index = this.myBetsStore.betsList.indexOf(bet);

				this.myBetsStore.betsList[index] = details;
				this.currentBetIndex = index;

				analyticsTracker.trackBetSlipView(
					bet.id,
					bet.betSlipStatus.abrv,
					bet.payment,
					bet.maxGain,
					bet.betStatus.abrv,
					bet.betSlipNumber
				);

				this.loader.resume();
			});
		} else {
			runInAction(() => {
				let index = this.myBetsStore.betsList.indexOf(bet);

				this.currentBetIndex = index;

				analyticsTracker.trackBetSlipView(
					bet.id,
					bet.betSlipStatus.abrv,
					bet.payment,
					bet.maxGain,
					bet.betStatus.abrv,
					bet.betSlipNumber
				);

				this.loader.resume();
			});
		}
	}

	@action.bound
	refresh() {
		if (this.myBetsStore.betsList[this.currentBetIndex] == null) {
			return;
		}
		this.setCurrentBet(this.myBetsStore.betsList[this.currentBetIndex]);
	}

	@action.bound
	async initBetCombinationForBetSlip(bet: BetViewType) {
		if (this.betCombinationsMap.has(bet.id)) {
			return;
		}

		this.betCombinationsMap.set(bet.id, null);
		let userAccount = getWalletByShopIdAndAccountTypeId(
			bet.shopId,
			bet.bettingAccountTypeId,
			this.rootStore.userAuthStore.userAccounts || []
		);
		let context = this.rootStore.userAuthStore.mapUserIdToUserContext(
			bet.userId
		);
		if (userAccount) {
			context = createApiRequestOptions(
				RequestContextType.Wallet,
				userAccount.id
			);
		}
		const combinations = await (
			await BetSlipService
		).default.getSystemCombinations(bet.id, context);

		this.betCombinationsMap.set(bet.id, combinations);
	}

	@action.bound
	openModal(currentBet: BetViewType, walletId: string) {
		this.isModalOpen = true;

		this.onInitialize(walletId);
		this.setCurrentBet(currentBet);
	}

	@action.bound
	closeModal() {
		this.onDispose();
		this.isModalOpen = false;

		this.resetSubComponentStates();
	}

	@action.bound
	resetSubComponentStates() {
		this.isBetCancelOpen = false;
		this.isCashoutOpen = false;
		this.isShareActive = false;
		this.isReuseOpen = false;

		this.myBetsStore.betCancelStore?.onExitBetCancelBetDetailsModal();
		this.betCancelStore?.onExitBetCancelBetDetailsModal();

		this.myBetsStore.cashoutStore.closeCashoutModal();
	}

	@action.bound
	onNewPage() {
		if (this.isModalOpen) {
			if (this.lastUsedDirection == "right") {
				this.setCurrentBet(this.myBetsStore.betsList[0]);
			}

			if (this.lastUsedDirection == "left") {
				this.setCurrentBet(
					this.myBetsStore.betsList[this.myBetsStore.pageSize - 1]
				);
			}
		}
	}

	@action.bound
	async moveRight() {
		this.lastUsedDirection = "right";

		if (this.currentBetIndex === this.myBetsStore.betsList.length - 1) {
			this.currentBetIndex = -1;
			this.myBetsStore.onPageChange(++this.myBetsStore.pageNumber);

			return;
		}

		const nextBet = this.myBetsStore.betsList[this.currentBetIndex + 1];
		this.setCurrentBet(nextBet);
	}

	@action.bound
	async moveLeft() {
		this.lastUsedDirection = "left";

		if (this.currentBetIndex === 0) {
			this.currentBetIndex = -1;
			this.myBetsStore.onPageChange(--this.myBetsStore.pageNumber);

			return;
		}

		const nextBet = this.myBetsStore.betsList[this.currentBetIndex - 1];
		this.setCurrentBet(nextBet);
	}

	@action.bound
	async submitCashout(data: CashOutSubmitType) {
		await this.myBetsStore.cashoutStore.cashout(
			data,
			CashoutLocation.MY_BETS
		);

		this.closeCashout();
		this.closeModal();
	}

	@action.bound
	openCashout(data: OpenCashoutModalType) {
		this.isCashoutOpen = true;

		this.myBetsStore.cashoutStore.openBetDetailsCashout(data);
	}

	@action.bound
	closeCashout() {
		this.isCashoutOpen = false;
	}

	@action.bound
	cancelCashout() {
		this.closeCashout();

		this.myBetsStore.cashoutStore.closeBetDetailsCashout();
	}

	@action.bound
	onBetCancelOpen() {
		this.isBetCancelOpen = true;
	}

	@action.bound
	onBetCancelClose() {
		this.isBetCancelOpen = false;
	}

	@action.bound
	onBetCancelCloseStay() {
		this.isBetCancelOpen = false;
	}

	@action.bound
	onAutoSellOpen() {
		this.isAutoSellOpen = true;
	}

	@action.bound
	onAutoSellClose() {
		this.isAutoSellOpen = false;
	}

	@action.bound
	onReuseOpen() {
		this.isReuseOpen = true;
	}

	@action.bound
	onReuseClose() {
		this.isReuseOpen = false;
	}

	@action.bound
	onShareOpen() {
		this.isShareActive = true;
	}

	@action.bound
	onShareClose() {
		this.isShareActive = false;
	}
	//#endregion actions

	//#region dispose
	@action.bound
	onDispose() {
		this.betCancelStore.onDispose();
		this.activeEventsStore.onDispose();
		this.betCombinationsMap.clear();
		this.isStoreInitialized = false;
		this.walletId = RequestContextType.Default;
		this.shareBetSlipStore.onDispose();
		this.lastUsedDirection = null;
		this.currentBetIndex = -1;
	}
	//#endregion dispose
}

export type CashOutSubmitType = {
	id: string;
	calculatedCashoutAmount: number;
	displayedAmount: string;
	cType: CashoutType;
	isChangeAccepted: boolean;
	isLive: boolean;
	userId: string;
	liveBetSlipCountdown: number | null;
};
